// Hộp hướng dẫn cho trang có NHIỀU THẺ, mỗi thẻ một việc rời nhau.
//
// Dùng chung cho trang Thanh toán và ba trang ca. Trước đây mỗi trang một hộp
// chép tay; hai hộp đầu đã kèm lời dặn "đến ngoại lệ thứ ba thì dừng chép tay,
// đổi sang bảng tra" — ba trang ca tới cùng lúc chính là lúc đó.
//
// Ngưỡng để một trang dùng hộp này: phải có CẢ HAI — nhiều thẻ, VÀ mỗi thẻ một
// việc rời nhau. Người đang dọn hàng "Tiền về chưa rõ đơn" không cần một chữ
// nào của thẻ "Cấu hình", mà hộp lại mở đúng thẻ họ đang đứng, nên các thẻ kia
// thuần tuý là nhiễu. Chỉ "bài hơi dài" thì dùng hộp chung `GuideDialog`.
//
// Phần riêng của từng trang — thứ tự thẻ, ai mở được thẻ nào, màu nhãn, dải
// vòng đời — khai trong `multi-tab-guides.ts`. Cách bày nằm ở đây, một bản cho
// tất cả: sửa một chỗ là cả bốn trang theo, không lệch nhau.

import { useState } from "react";
import { HelpCircle } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/tabs";
import { useAuthV2 } from "@/lib/authv2/store";

import { audiencesOf, type Audience } from "./audience";
import { GuideBlockList, GuideBulletList, GuideStatusList } from "./GuideSections";
import { GuideFlowStrip, type NhanThe } from "./GuideFlowStrip";
import { MULTI_TAB_GUIDES, type MultiTabGuideConfig } from "./multi-tab-guides";
import type { GuideTab, PageGuide } from "./types";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  guide: PageGuide;
  /** Thẻ người đọc đang đứng trên màn, để hộp mở thẳng vào bài của thẻ đó. */
  guideTab?: string | undefined;
  /** Id trang, để tra phần khai riêng của nó trong bảng. */
  guideId: string;
};

export function MultiTabGuideDialog({ open, onOpenChange, title, guide, guideTab, guideId }: Props) {
  const staff = useAuthV2((s) => s.currentStaff);
  const auds = audiencesOf(staff);
  const cfg = MULTI_TAB_GUIDES[guideId];

  // Chỉ giữ thẻ người đọc MỞ ĐƯỢC trên màn thật. Cách rào này không nói một
  // chữ nào về quyền: thẻ không dành cho họ thì đơn giản không xuất hiện.
  //
  // ⚠️ KHÔNG thêm nhánh "không còn thẻ nào thì hiện hết". Bản đầu của trang
  // Thanh toán có nhánh đó và người không mở được thẻ nào (hậu kỳ, chăm sóc
  // khách) đọc trọn bộ chín thẻ — đúng cái rò mà tính năng này sinh ra để
  // chặn. Danh sách rỗng thì bày đúng một dòng nói trang này chưa có phần của họ.
  const dsThe = (guide.tabs ?? []).filter((t) => moDuoc(cfg, t.key, auds));

  const macDinh = chonThe(dsThe, guideTab);
  const [dangMo, setDangMo] = useState<string>(macDinh);

  // Thẻ đang đứng đổi trong lúc hộp đóng thì lần mở sau phải theo thẻ mới.
  // Chỉnh thẳng trong lượt vẽ (cách React khuyên khi state phải theo prop) —
  // qua hiệu ứng thì người dùng thấy chớp một nhịp thẻ cũ.
  const [daThay, setDaThay] = useState({ open, macDinh });
  if (open !== daThay.open || macDinh !== daThay.macDinh) {
    setDaThay({ open, macDinh });
    if (open) setDangMo(macDinh);
  }

  const the = dsThe.find((t) => t.key === dangMo) ?? dsThe[0];
  const nhan: NhanThe = Object.fromEntries(dsThe.map((t) => [t.key, t.label]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="size-5 shrink-0 text-muted" aria-hidden />
            <span className="min-w-0">Hướng dẫn: {title}</span>
          </DialogTitle>
        </DialogHeader>

        <p className="rounded-md bg-muted/10 p-3 text-sm leading-relaxed">{guide.purpose}</p>

        {/* Không mở được thẻ nào: nói đúng một câu rồi thôi. Đây là kết cục
            ĐÚNG với người ghé qua trang này, không phải hộp hỏng — thà thiếu
            còn hơn phát bài của việc người khác. */}
        {dsThe.length === 0 && (
          <p className="text-sm text-muted">
            Trang này chưa có phần dành cho công việc của bạn. Cần dùng tới thì hỏi quản lý trực
            tiếp.
          </p>
        )}

        {/*
          `min-w-0` KHÔNG phải trang trí — bài học đã đo ở hộp trang lương: thân
          hộp thoại là lưới, mà ô lưới mặc định không co nhỏ hơn nội dung. Hàng
          thẻ giữ chữ trên một dòng nên min-content của nó là TỔNG bề rộng các
          thẻ, kéo cả cột rộng ra và đẩy mọi đoạn văn tràn khỏi màn điện thoại.
        */}
        <Tabs
          value={the?.key ?? ""}
          onValueChange={setDangMo}
          className={`min-w-0 space-y-4 text-sm ${dsThe.length === 0 ? "hidden" : ""}`}
        >
          {/*
            Hai điều khiển cho CÙNG một state, đổi theo khổ màn. Nhiều thẻ nhãn
            dài xếp thành hàng chip thì ở khổ điện thoại phải cuộn ngang và bấm
            mò; ô chọn đọc được hết tên trong một chạm. Từ md trở lên thì hàng
            chip tự xuống dòng (`md:flex-wrap` có sẵn trong ui/tabs) nên nhìn
            một phát thấy hết, hơn hẳn ô chọn.
          */}
          <div className="md:hidden">
            <Select value={the?.key ?? ""} onValueChange={setDangMo}>
              <SelectTrigger aria-label="Chọn thẻ để xem hướng dẫn">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {dsThe.map((t) => (
                  <SelectItem key={t.key} value={t.key}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <TabsList className="hidden md:flex" aria-label={`Thẻ của trang ${title}`}>
            {dsThe.map((t) => (
              <TabsTrigger key={t.key} value={t.key}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {dsThe.map((t) => (
            <TabsContent key={t.key} value={t.key} className="space-y-5">
              <NoiDungThe the={t} auds={auds} nhan={nhan} cfg={cfg} />
            </TabsContent>
          ))}
        </Tabs>

        <p className="border-t border-border pt-3 text-sm text-muted">
          {guide.askWho ?? "Còn vướng chỗ nào thì hỏi quản lý trực tiếp của bạn."}
        </p>
      </DialogContent>
    </Dialog>
  );
}

function NoiDungThe({
  the,
  auds,
  nhan,
  cfg,
}: {
  the: GuideTab;
  auds: Audience[];
  nhan: NhanThe;
  cfg: MultiTabGuideConfig | undefined;
}) {
  const blocks = (the.blocks ?? []).filter((b) => b.for.some((a) => auds.includes(a)));

  return (
    <>
      <p
        className="rounded-md border-l-4 p-3 leading-relaxed"
        style={{
          background: "var(--color-brand-glow)",
          borderLeftColor: "var(--color-brand)",
          color: "var(--color-brand-dark)",
        }}
      >
        {the.purpose}
      </p>

      {cfg?.flow && <GuideFlowStrip flow={cfg.flow} dangMo={the.key} nhan={nhan} />}

      {the.where && the.where.length > 0 && (
        <Phan heading="Trên thẻ này có gì, ở đâu">
          <GuideBulletList items={the.where} />
        </Phan>
      )}

      {the.howItWorks && the.howItWorks.length > 0 && (
        <Phan heading="Cách thẻ này chạy">
          <GuideBulletList items={the.howItWorks} />
        </Phan>
      )}

      {blocks.length > 0 && <GuideBlockList blocks={blocks} />}

      {the.statuses && the.statuses.length > 0 && (
        <Phan heading="Các trạng thái nghĩa là gì">
          <GuideStatusList
            statuses={the.statuses}
            labelWidth="sm:w-44"
            tone={(label) => cfg?.tone?.[label]}
          />
        </Phan>
      )}
    </>
  );
}

function Phan({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 font-semibold">{heading}</h3>
      {children}
    </section>
  );
}

/**
 * Người đọc mở được thẻ này trên màn thật không.
 *
 * Hỏi bảng phạm vi chứ KHÔNG suy từ "thẻ này có khối nào của họ": hai thứ lệch
 * nhau, và lệch theo hướng nguy hiểm — xem chú thích ở `multi-tab-guides.ts`.
 * Trang chưa khai bảng thì mọi thẻ đều đóng, vì thà thiếu còn hơn phát nhầm.
 */
function moDuoc(cfg: MultiTabGuideConfig | undefined, key: string, auds: Audience[]): boolean {
  const cho = cfg?.theAiMoDuoc[key];
  return cho ? cho.some((a) => auds.includes(a)) : false;
}

/**
 * Thẻ mở sẵn: đúng thẻ người đọc đang đứng nếu họ đọc được nó.
 *
 * Không đọc được (thẻ của việc khác) thì rơi về thẻ đầu tiên chứ không mở một
 * thẻ trống — và cũng không nói vì sao, vì "vì sao" là chuyện quyền.
 */
function chonThe(dsThe: GuideTab[], guideTab: string | undefined): string {
  const dau = dsThe[0]?.key ?? "";
  if (!guideTab) return dau;
  const khop = dsThe.find((t) => t.key === guideTab);
  return khop ? khop.key : dau;
}
